import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { clientsApi, ClientBalanceResponse } from '../../services/clients';
import { casesApi } from '../../services/cases';
import { Client, Case, InvoiceStatus } from '../../types';

// Helper to format cents as currency
const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

export const ClientDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [cases, setCases] = useState<Case[]>([]);
  const [balance, setBalance] = useState<ClientBalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (id) loadData(id);
  }, [id]);

  const loadData = async (clientId: string) => {
    try {
      setLoading(true);
      const [clientData, casesData, balanceData] = await Promise.all([
        clientsApi.get(clientId),
        casesApi.list(clientId),
        clientsApi.getBalance(clientId).catch(() => null),
      ]);
      setClient(clientData);
      setCases(casesData.cases);
      setBalance(balanceData);
    } catch (err) {
      setError('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, colors: Record<string, string>) => (
    <span style={{
      backgroundColor: colors[status] || '#9e9e9e',
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );

  const clientStatusColors = { prospect: '#2196f3', active: '#4caf50', inactive: '#ff9800', archived: '#9e9e9e' };
  const caseStatusColors = { active: '#4caf50', closed: '#9e9e9e', 'on-hold': '#ff9800' };
  const invoiceStatusColors: Record<InvoiceStatus, string> = { 
    draft: '#9e9e9e', 
    sent: '#2196f3', 
    paid: '#4caf50', 
    overdue: '#d32f2f', 
    cancelled: '#757575' 
  };

  const handleInvite = async () => {
    if (!id || !client) return;
    if (!window.confirm(`Send portal invite to ${client.email}?`)) return;
    
    try {
      setInviting(true);
      await clientsApi.invite(id);
      alert(`Portal invite sent to ${client.email}`);
      loadData(id); // Refresh to show invited status
    } catch (err: any) {
      alert(err.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const canInvite = client?.status === 'active' && client?.email;

  if (loading) return <Layout><div style={{ padding: '24px' }}>Loading...</div></Layout>;
  if (error || !client) return <Layout><div style={{ padding: '24px', color: 'red' }}>{error || 'Client not found'}</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <button onClick={() => navigate('/clients')} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', marginBottom: '8px' }}>
              ← Back to Clients
            </button>
            <h1 style={{ margin: 0 }}>{client.lastName}, {client.firstName}</h1>
            <div style={{ marginTop: '8px' }}>{getStatusBadge(client.status, clientStatusColors)}</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canInvite && (
              <button
                onClick={handleInvite}
                disabled={inviting}
                style={{ backgroundColor: '#9c27b0', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: inviting ? 'not-allowed' : 'pointer' }}
              >
                {inviting ? 'Sending...' : '📧 Invite to Portal'}
              </button>
            )}
            <button
              onClick={() => navigate(`/clients/${id}/edit`)}
              style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}
            >
              Edit Client
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0 }}>Contact Information</h3>
            <p><strong>Email:</strong> {client.email || '-'}</p>
            <p><strong>Phone:</strong> {client.phone || '-'}</p>
            {client.address && (
              <p><strong>Address:</strong><br />
                {client.address.street && <>{client.address.street}<br /></>}
                {client.address.city && <>{client.address.city}, </>}
                {client.address.state} {client.address.postalCode}
              </p>
            )}
          </div>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0 }}>Notes</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{client.notes || 'No notes'}</p>
          </div>
        </div>

        {/* Balance Summary Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px', 
          marginBottom: '32px' 
        }}>
          <div style={{ 
            backgroundColor: balance && balance.totalBalance > 0 ? '#ffebee' : '#e8f5e9', 
            padding: '16px', 
            borderRadius: '8px',
            border: `2px solid ${balance && balance.totalBalance > 0 ? '#d32f2f' : '#4caf50'}`,
          }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>Total Balance Due</h3>
            <p style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              margin: 0,
              color: balance && balance.totalBalance > 0 ? '#d32f2f' : '#4caf50',
            }}>
              {formatCurrency(balance?.totalBalance || 0)}
            </p>
          </div>
          <div style={{ 
            backgroundColor: '#e3f2fd', 
            padding: '16px', 
            borderRadius: '8px',
            border: '2px solid #1976d2',
          }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>Available Credit</h3>
            <p style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              margin: 0,
              color: '#1976d2',
            }}>
              {formatCurrency(balance?.availableCredit || 0)}
            </p>
            {(balance?.availableCredit || 0) > 0 && (
              <p style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginBottom: 0 }}>
                From overpayments - can be applied to future invoices
              </p>
            )}
          </div>
        </div>

        {/* Invoices Section */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ margin: '0 0 16px 0' }}>Invoices ({balance?.invoices?.length || 0})</h2>
          {!balance?.invoices || balance.invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              No invoices yet.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ textAlign: 'left', padding: '12px 8px' }}>Invoice #</th>
                  <th style={{ textAlign: 'right', padding: '12px 8px' }}>Total</th>
                  <th style={{ textAlign: 'right', padding: '12px 8px' }}>Balance Due</th>
                  <th style={{ textAlign: 'left', padding: '12px 8px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {balance.invoices.map(inv => (
                  <tr
                    key={inv.invoiceId}
                    onClick={() => navigate(`/invoices/${inv.invoiceId}`)}
                    style={{ borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '12px 8px' }}>{inv.invoiceNumber}</td>
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatCurrency(inv.total)}</td>
                    <td style={{ 
                      padding: '12px 8px', 
                      textAlign: 'right',
                      color: inv.balanceDue > 0 ? '#d32f2f' : '#4caf50',
                      fontWeight: inv.balanceDue > 0 ? 'bold' : 'normal',
                    }}>
                      {formatCurrency(inv.balanceDue)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {getStatusBadge(inv.status, invoiceStatusColors)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>Cases ({cases.length})</h2>
          <button
            onClick={() => navigate(`/cases/new?clientId=${id}`)}
            style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
          >
            + New Case
          </button>
        </div>

        {cases.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            No cases yet. Click "New Case" to add one.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Child Name</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>School District</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Grade</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {cases.map(c => (
                <tr
                  key={c.caseId}
                  onClick={() => navigate(`/cases/${c.caseId}`)}
                  style={{ borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }}
                  onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                  onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ padding: '12px 8px' }}>{c.childLastName}, {c.childFirstName}</td>
                  <td style={{ padding: '12px 8px' }}>{c.schoolDistrict || '-'}</td>
                  <td style={{ padding: '12px 8px' }}>{c.grade || '-'}</td>
                  <td style={{ padding: '12px 8px' }}>{getStatusBadge(c.status, caseStatusColors)}</td>
                  <td style={{ padding: '12px 8px' }}>{new Date(c.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};
